import type { QuizBuilderOptionsSchema } from '@repo/schemas/quiz/quizBuilder';
import type { GroupPublic } from '@repo/types/groups';
import type { MemberChanges } from '@repo/types/memberChanges';
import type { UserPublic } from '@repo/types/users';

import { useErrorMessage } from '@repo/api/error-message/useErrorMessage';
import { useEditQuiz } from '@repo/api/quiz/useEditQuiz';
import { useEditQuizMemberChanges } from '@repo/api/quiz/useEditQuizMemberChanges';
import { ErrorCodes } from '@repo/lib/errorCodes';
import { quizBuilderErrorMessages } from '@repo/lib/messages/quizBuilderErrorMessages';
import { quizBuilderSchemaParser } from '@repo/schemas/quiz/quizBuilder';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import { useQuizBuilder } from '@/context/quiz-builder';

export function useQuizBuilderSubmit(users: UserPublic[], groups: GroupPublic[]) {
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const { quizId } = useParams();
  const { submitOptions, buildQuizPayload, removeQuizDraftData, quizQuery } = useQuizBuilder();
  const navigate = useNavigate();

  const editQuizMutation = useEditQuiz(quizId ?? '');
  const editQuizMemberChangesMutation = useEditQuizMemberChanges();

  const isPending: boolean = editQuizMutation.isPending || editQuizMemberChangesMutation.isPending;

  const isError: boolean = editQuizMutation.isError || editQuizMemberChangesMutation.isError;

  const error: Error | null = editQuizMutation.error || editQuizMemberChangesMutation.error;

  const errorMessage = useErrorMessage(error, quizBuilderErrorMessages);

  const onSubmit = async (data: QuizBuilderOptionsSchema, force: boolean = false) => {
    submitOptions(data);
    const payload = buildQuizPayload();
    try {
      const parsePayload = quizBuilderSchemaParser.parse(payload);
      await editQuizMutation.mutateAsync({ ...parsePayload, force });
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.data.code === ErrorCodes.FORBIDDEN.QUIZ.ANSWER_INVALIDATION) {
          setOpenAlert(true);
        }
      } else {
        toast.error('Wystąpił nieoczekiwany błąd');
      }
      return;
    }

    const quizQueryData = quizQuery.data;

    const memberChanges: MemberChanges[] = [
      {
        userIds: users.filter((formUser) => !(quizQueryData?.users || []).some((u) => u.id === formUser.id)).map((u) => u.id),
        groupIds: groups.filter((formGroup) => !(quizQueryData?.groups || []).some((g) => g.id === formGroup.id)).map((g) => g.id),
        add: true,
        remove: false,
      },
      {
        userIds: (quizQueryData?.users || []).filter((queryUser) => !users.some((u) => u.id === queryUser.id)).map((u) => u.id),
        groupIds: (quizQueryData?.groups || []).filter((queryGroup) => !groups.some((g) => g.id === queryGroup.id)).map((g) => g.id),
        add: false,
        remove: true,
      },
    ];

    if (
      !(
        (memberChanges[0]?.userIds ?? []).length === 0 &&
        (memberChanges[0]?.groupIds ?? []).length === 0 &&
        (memberChanges[1]?.userIds ?? []).length === 0 &&
        (memberChanges[1]?.groupIds ?? []).length === 0
      )
    ) {
      await editQuizMemberChangesMutation.mutateAsync({
        memberChanges: memberChanges,
        id: quizId ?? '',
      });
    }

    removeQuizDraftData();
    navigate('/manage/quizzes');
  };

  return {
    onSubmit,
    isPending,
    isError,
    errorMessage,
    openAlert,
    setOpenAlert,
  };
}
